import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import {
  useSignup,
  useSignupViaJoin,
} from "@orbit/api-client-react";
import { canonicalizePhone, formatPhDisplay } from "../lib/phone";
import { useAuth } from "../hooks/useAuth";
import { AuthLayout } from "../components/AuthLayout";

function useTenantParam(): string | null {
  const search = typeof window !== "undefined" ? window.location.search : "";
  const params = new URLSearchParams(search);
  return params.get("tenant");
}

export function SignupPage() {
  const tenant = useTenantParam();
  // Two distinct flows live under /signup:
  //   • ?tenant=ABCDE  → QR-bound counter signup, single submit; the
  //     merchant scans the customer's phone and the customer accepts in
  //     one step. Atomically creates the customer + their first
  //     tenant_customers membership.
  //   • no tenant      → public signup, two-step OTP. The customer is
  //     arriving via a social-media post or shared link with no merchant
  //     context. We materialize the global customer record on OTP
  //     verify, no tenant_customers row — the empty Cards page will
  //     prompt them to find a participating merchant.
  if (tenant) return <JoinSignupForm tenantShortCode={tenant} />;
  return <PublicSignupForm />;
}

function JoinSignupForm({ tenantShortCode }: { tenantShortCode: string }) {
  const [, navigate] = useLocation();
  const { signIn } = useAuth();

  const [name, setName] = useState("");
  const [phoneRaw, setPhoneRaw] = useState("");
  // Phase 32 (D104) — optional at the QR counter so we don't slow the
  // staff down. The post-signup /set-password page nudges for it.
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const signupMut = useSignupViaJoin();

  const canonical = useMemo(() => canonicalizePhone(phoneRaw), [phoneRaw]);
  const emailTrimmed = email.trim();
  const canSubmit =
    name.trim().length > 0 && canonical !== null && consent && !signupMut.isPending;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!canonical) {
      setSubmitError("Please enter a valid PH mobile number.");
      return;
    }
    try {
      const result = await signupMut.mutateAsync({
        tenantShortCode,
        name: name.trim(),
        phone: canonical,
        // Only attach when the customer typed one — empty string would
        // fail Zod's email parse on the server.
        ...(emailTrimmed ? { email: emailTrimmed } : {}),
        consentAcknowledged: true,
      });
      signIn(result.customerId, result.tokens);
      // Phase 31 (D103) — signup-via-join creates the customer
      // passwordless (no `password` field on the request). Route them
      // through /set-password before /cards so every account ends with
      // a credential. The page exposes a "Skip for now" link, so this
      // is a nudge, not a hard block.
      navigate("/set-password?next=/cards");
    } catch (err) {
      const e = err as { status?: number; apiError?: { code?: string; message?: string } };
      // Phase 32 (D104) — distinguish the two 409 reasons explicitly.
      if (e.status === 409 && e.apiError?.code === "EMAIL_ALREADY_REGISTERED") {
        setSubmitError("That email is already used by another Orbit account.");
      } else if (e.status === 409) {
        setSubmitError("This phone number is already registered. Sign in instead.");
      } else if (e.status === 404) {
        setSubmitError("Merchant not found.");
      } else {
        setSubmitError(e.apiError?.message ?? "Couldn't sign you up. Try again.");
      }
    }
  };

  return (
    <AuthLayout title="Create your card" subtitle="Takes about 30 seconds.">
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <div>
          <label htmlFor="name" className="label">Name</label>
          <input
            id="name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Juan dela Cruz"
            autoComplete="name"
            required
          />
        </div>
        <div>
          <label htmlFor="phone" className="label">Mobile number</label>
          <input
            id="phone"
            className="input"
            value={formatPhDisplay(phoneRaw)}
            onChange={(e) => setPhoneRaw(e.target.value)}
            placeholder="0917-123-4567"
            inputMode="tel"
            autoComplete="tel"
            required
          />
        </div>
        <div>
          <label htmlFor="email" className="label">
            Email <span className="text-ink-dark/60 font-normal">(optional)</span>
          </label>
          <input
            id="email"
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
          />
          <p className="mt-1 text-xs text-ink-dark/70">
            Helps recover your account later. You can also add it
            from your profile.
          </p>
        </div>
        <label className="flex items-start gap-3 text-sm text-navy">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-ink-mid text-navy focus:ring-navy"
            required
          />
          <span>
            I agree to Orbit's{" "}
            <a href="#terms" className="underline text-navy">Terms</a> and{" "}
            <a href="#privacy" className="underline text-navy">Privacy Notice</a>.
          </span>
        </label>
        {submitError && <p className="text-red-600 text-sm">{submitError}</p>}
        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {signupMut.isPending ? "Signing up…" : "Sign Up"}
        </button>
        <p className="text-center text-sm text-ink-dark">
          Already have an Orbit card?{" "}
          <a
            href={`/login?tenant=${encodeURIComponent(tenantShortCode)}`}
            className="underline text-navy"
          >
            Sign in
          </a>
        </p>
      </form>
    </AuthLayout>
  );
}

function PublicSignupForm() {
  const [, navigate] = useLocation();
  const { signIn } = useAuth();

  // One-shot signup: name + phone + password + email, submitted in
  // a single POST. The server creates the customer with
  // phoneVerified=false and emailVerified=false; both badges are
  // unlocked later from the profile page via the verify-{phone,email}
  // flow. No SMS_NOT_CONFIGURED gate here — signup itself never sends
  // an OTP. Phase 32 (D104) made email REQUIRED so every account
  // ships with two recovery channels from day one.
  const [name, setName] = useState("");
  const [phoneRaw, setPhoneRaw] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signupMut = useSignup();
  const canonical = useMemo(() => canonicalizePhone(phoneRaw), [phoneRaw]);
  // Mirrors lib/api-zod CustomerPasswordSchema.min(12). Keep the
  // numbers literally identical — drift here would let users type
  // a "valid" 10–11 char password that the server then 422s on.
  const passwordOk = password.length >= 12;
  const emailTrimmed = email.trim();
  // Loose client-side email check — the server's Zod
  // EmailSchema is the source of truth. We only block obvious
  // empties + missing-`@` so the submit button isn't a dead-end.
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed);
  const canSubmit =
    name.trim().length > 0 &&
    canonical !== null &&
    passwordOk &&
    emailOk &&
    consent &&
    !signupMut.isPending;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!canonical) {
      setError("Please enter a valid PH mobile number.");
      return;
    }
    if (!passwordOk) {
      setError("Password must be at least 12 characters.");
      return;
    }
    if (!emailOk) {
      setError("Please enter a valid email address.");
      return;
    }
    try {
      const r = await signupMut.mutateAsync({
        name: name.trim(),
        phone: canonical,
        password,
        // Phase 32 (D104) — required field; no longer guarded.
        email: emailTrimmed,
        consentAcknowledged: true,
      });
      signIn(r.customerId, r.tokens);
      navigate("/cards");
    } catch (err) {
      const e = err as {
        status?: number;
        apiError?: { code?: string; message?: string };
      };
      // Backend distinguishes the two 409 reasons with explicit codes:
      // EMAIL_ALREADY_REGISTERED vs PHONE_ALREADY_REGISTERED. Keep the
      // string match aligned or the email-dup case will silently
      // surface as a phone-dup message.
      if (e.status === 409 && e.apiError?.code === "EMAIL_ALREADY_REGISTERED") {
        setError("That email is already used by another Orbit account.");
      } else if (e.status === 409) {
        setError("This phone number is already registered. Sign in instead.");
      } else if (e.status === 429) {
        setError("Too many attempts. Try again in a few minutes.");
      } else if (e.status === 422) {
        setError(e.apiError?.message ?? "Please check the form and try again.");
      } else {
        setError(e.apiError?.message ?? "Couldn't sign you up. Try again.");
      }
    }
  };

  return (
    <AuthLayout title="Create your Orbit" subtitle="One card. Many merchants.">
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <div>
          <label htmlFor="name" className="label">Name</label>
          <input
            id="name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Juan dela Cruz"
            autoComplete="name"
            required
          />
        </div>
        <div>
          <label htmlFor="phone" className="label">Mobile number</label>
          <input
            id="phone"
            className="input"
            value={formatPhDisplay(phoneRaw)}
            onChange={(e) => setPhoneRaw(e.target.value)}
            placeholder="0917-123-4567"
            inputMode="tel"
            autoComplete="tel"
            required
          />
        </div>
        <div>
          <label htmlFor="password" className="label">Password</label>
          <div className="relative">
            <input
              id="password"
              className="input pr-16"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={12}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-2 my-auto text-xs font-medium text-navy underline"
              tabIndex={-1}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
          <p className="mt-1 text-xs text-ink-dark/70">
            At least 12 characters. Mix it up — you'll use this to sign in.
          </p>
        </div>
        <div>
          <label htmlFor="email" className="label">Email</label>
          <input
            id="email"
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            required
          />
          <p className="mt-1 text-xs text-ink-dark/70">
            Used to recover your account if you lose your phone.
          </p>
        </div>
        <label className="flex items-start gap-3 text-sm text-navy">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-ink-mid text-navy focus:ring-navy"
            required
          />
          <span>
            I agree to Orbit's{" "}
            <a href="#terms" className="underline text-navy">Terms</a> and{" "}
            <a href="#privacy" className="underline text-navy">Privacy Notice</a>.
          </span>
        </label>
        {error && <p className="text-red-600 text-sm">{error}</p>}
        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {signupMut.isPending ? "Creating…" : "Create my Orbit"}
        </button>
        <p className="text-center text-sm text-ink-dark">
          Already have an Orbit card?{" "}
          <a href="/login" className="underline text-navy">
            Sign in
          </a>
        </p>
      </form>
    </AuthLayout>
  );
}
